import React, { useEffect, useState, useRef } from "react"; // Import React and necessary hooks

// Import Material-UI components and icons
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  Paper,
  Box,
  TextField,
  CircularProgress,
  LinearProgress,
  Checkbox,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

// Import necessary data and components
import {
  baseYears,
  metrics,
  scopesCoveredOptions,
  targetYears,
} from "./data/ProjectRegisterData";
import ProjectRegisterTable from "./ProjectRegisterTable";
import ProjectMetrics from "./ProjectMetrics";
import { FilterMetric } from "./data/FilterMetric";
import { _getMarginalAbatementCost } from "middlewares/DashboardApi/projectMetrics";
import { MarginalAbatementCostModel } from "./data/MaginalAbatementResult";
import { useParams } from "react-router-dom";
import { Constants, ProjectRegisterMetrics } from "constants/constant";
import { _getLocation } from "middlewares/LocationApi/location";
import MarginalAbatementCostCurve , { MarginalAbatementCostCurveRef } from "./MarginalAbatementCostCurve";
import MeasureImpactsbyScope, { MeasureImpactsbyScopeRef } from "./MeasureImpactsbyScope";
import { MIBScopeData } from "./data/MIBScopeData";
import TimelineAndFinancialMetrics, { TimelineAndFinancialMetricsRef } from "./TimelineAndFinancialMetrics";
import { TLFMetricsData } from "./data/TLFMetricsData";
import { MACgraphModel } from "./data/MACgraphModel";
import { generateData } from "./data/metricsUtils";
import InteractiveGlidepath, { InteractiveGlidepathRef } from "./InteractiveGlidepath";
import ProjectRegisterDropdown from "pages/PortfolioView/ProjectRegisterDropdown";
import { InteractiveGlidepathMetric } from "./data/InteractiveGlidepathMetric";
import { useAppState } from "context/appState.context";
import { ProjectRegisterGroupBy } from "./data/projectRegisterTableUtils";
import { getMacGraphDataByGroup } from "./data/macGraphUtils";
import { getTimelineFinancialMetricByGroup } from "./data/timelineFinancialUtils";
import { normalizeMeasureLabel } from "./data/measureLabelUtils";

import ChartDownloadButtonMenu from "components/ChartDownloadButtons/ChartDownloadButtons";
import NotificationHelper from "services/notification-helper";
import { ApiError } from "object-models/shared/api-error.model";

const NON_MEASURE_WATERFALL_LABELS = new Set(
  ["BAU Emissions", "Residual emissions", "Historical Emissions", "Growth"].map(
    normalizeMeasureLabel
  )
);

const GROUP_BY_OPTIONS = [
  { label: "Measure Name", value: "Name" },
  { label: "Measure Type", value: "Type" },
  { label: "Measure Category", value: "Category" },
];

type RangeFilterConfig = {
  title: string;
  subtitle: string;
  accentGradient: string;
  accentColor: string;
  range: "emission" | "marginal";
  inputs: { min: string; max: string };
  absoluteMin: number;
  absoluteMax: number;
  minTestId: string;
  maxTestId: string;
};

export const isValidPartialNumberInput = (value: string) => {
  if (value === "") {
    return true;
  }

  let startIndex = 0;
  let hasDecimalPoint = false;

  if (value.startsWith("-")) {
    startIndex = 1;
    if (value.length === 1) {
      return true;
    }
  }

  for (let index = startIndex; index < value.length; index += 1) {
    const character = value[index];

    if (character === ".") {
      if (hasDecimalPoint) {
        return false;
      }

      hasDecimalPoint = true;
      continue;
    }

    if (character < "0" || character > "9") {
      return false;
    }
  }

  return true;
};

export const getNextRangeInputState = (
  previousState: { min: string; max: string },
  field: "min" | "max",
  value: string
) => {
  if (!isValidPartialNumberInput(value)) {
    return previousState;
  }

  return {
    ...previousState,
    [field]: value,
  };
};

export const getBoundedValue = (
  rawValue: string,
  absoluteMin: number,
  absoluteMax: number,
  fallbackValue: number,
  lowerLimit = absoluteMin,
  upperLimit = absoluteMax
) => {
  if (rawValue.trim() === "") {
    return fallbackValue;
  }

  const parsedValue = Number(rawValue);
  if (Number.isNaN(parsedValue)) {
    return fallbackValue;
  }

  const boundedLower = Math.max(absoluteMin, lowerLimit);
  const boundedUpper = Math.min(absoluteMax, upperLimit);

  return Math.min(Math.max(parsedValue, boundedLower), boundedUpper);
};

export const getCommittedRange = (
  currentRange: number[],
  inputs: { min: string; max: string },
  field: "min" | "max",
  absoluteMin: number,
  absoluteMax: number,
  rawValue?: string
) => {
  const nextRange = [...currentRange];
  const committedValue = rawValue ?? inputs[field];

  const nextValue = getBoundedValue(
    committedValue,
    absoluteMin,
    absoluteMax,
    currentRange[field === "min" ? 0 : 1] ??
      (field === "min" ? absoluteMin : absoluteMax),
    field === "min" ? absoluteMin : nextRange[0] ?? absoluteMin,
    field === "min" ? nextRange[1] ?? absoluteMax : absoluteMax
  );

  if (field === "min") {
    nextRange[0] = nextValue;
  } else {
    nextRange[1] = nextValue;
  }

  return nextRange;
};

export const formatErrorMessage = (error: unknown): string => {
  if (typeof error === "string") {
    return error;
  }

  const problemDetailsErrors = (error as any)?.response?.data?.errors;
  if (problemDetailsErrors && typeof problemDetailsErrors === "object" && !Array.isArray(problemDetailsErrors)) {
    const validationMessages = Object.values(problemDetailsErrors as Record<string, unknown>)
      .flatMap((value) => {
        if (Array.isArray(value)) {
          return value.filter((item): item is string => typeof item === "string");
        }

        if (typeof value === "string") {
          return [value];
        }

        return [] as string[];
      })
      .map((message) => message.trim())
      .filter(Boolean);

    if (validationMessages.length > 0) {
      return validationMessages.join(" ");
    }
  }

  const problemDetailsDetail = (error as any)?.response?.data?.detail;
  if (typeof problemDetailsDetail === "string" && problemDetailsDetail.trim()) {
    return problemDetailsDetail.trim();
  }

  const directMessage = (error as any)?.message;
  if (typeof directMessage === "string" && directMessage.trim()) {
    return directMessage.trim();
  }

  const nestedMessage = (error as any)?.response?.data?.message;
  if (typeof nestedMessage === "string" && nestedMessage.trim()) {
    return nestedMessage.trim();
  }

  return "Unable to load project register data.";
};

export const getChartTypeLabel = (option: string) => {
  switch (option) {
    case ProjectRegisterMetrics.MarginalAbatementCostCurve:
      return "Marginal Abatement Cost";
    case ProjectRegisterMetrics.MeasureImpactsbyScope:
      return "Decarbonization Waterfall";
    case ProjectRegisterMetrics.TimelineandFinancialMetrics:
      return "Decarbonization Pathway";
    case ProjectRegisterMetrics.InteractiveGlidepath:
      return "Target Trajectory";
    default:
      return "Chart";
  }
};

const hasCommittedRangeFilters = (state: FilterMetric) =>
  state.minEmissionReduction !== null ||
  state.maxEmissionReduction !== null ||
  state.minMAC !== null ||
  state.maxMAC !== null;

/**
 * ProjectRegister component is used to display and manage project registration data.
 * It includes filters, metrics, and different views such as Marginal Abatement Cost Curve,
 * Measure Impacts by Scope, and Decarbonization Pathway.
 *
 * @component
 * @returns {JSX.Element} The rendered component.
 */
const ProjectRegister = () => {
  // Params
  const { LocationId } = useParams();
  const { currency } = useAppState();
  const UNIT_TCO2E = Constants.UNIT_TCO2E;

  // State variables
  const [selectedOption, setSelectedOption] = useState(
    ProjectRegisterMetrics.MarginalAbatementCostCurve
  );
  const [selectedScope, setSelectedScope] = useState(
    scopesCoveredOptions.map((a) => a.id)
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<ProjectRegisterGroupBy>("Name");
  const [isGroupByLoading, setIsGroupByLoading] = useState(false);
  const [filterState, setFilterState] = useState<FilterMetric>({
    locationId: Number(LocationId),
    dashboardSection: selectedOption,
    ghgScope: scopesCoveredOptions.map((a) => a.value),
    startYear: null,
    endYear: null,
    scenario: null,
    businessUnit: null,
    minEmissionReduction: null,
    maxEmissionReduction: null,
    minMAC: null,
    maxMAC: null,
    groupBy: 'Name',
    scopes: selectedScope,
  });
  const [loading, setLoading] = useState(true);
  const [marginalAbatementCost, setMarginalAbatementCost] = useState<
    Array<MarginalAbatementCostModel>
  >([]);
  const [baseYear, setBaseYear] = useState<number | undefined>(undefined);
  const [targetYear, setTargetYear] = useState<number | undefined>(undefined);

  const [metricsData, setMetricsData] = useState(metrics);
  const [showFilters, setShowFilters] = useState(true);

  const [emissionReduction, setEmissionReduction] = React.useState<number[]>(
    []
  );
  const [marginalAbatement, setMarginalAbatement] = React.useState<number[]>(
    []
  );
  const [emissionInputs, setEmissionInputs] = useState({ min: "", max: "" });
  const [marginalInputs, setMarginalInputs] = useState({ min: "", max: "" });

  const [minEmission, setMinEmission] = React.useState<number>(0);
  const [maxEmission, setMaxEmission] = React.useState<number>(0);

  const [minMarginal, setMinMarginal] = React.useState<number>(0);
  const [maxMarginal, setMaxMarginal] = React.useState<number>(0);

  const [macData, setMacData] = useState<MACgraphModel[]>([]);
  const [mibScopeData, setMibScopeData] = useState<MIBScopeData[]>([]);
  const [tlfMetricsData, setTlfMetricsData] = useState<TLFMetricsData[]>([]);
  const [interactiveGlidepathData, setInteractiveGlidepathData] = useState<
    InteractiveGlidepathMetric[]
  >([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isMacDataLoaded, setIsMacDataLoaded] = useState(false);
  const [scenarioOptions, setScenarioOptions] = useState<string[]>([]);

  const chartMeasureOrder = React.useMemo(() => {
    if (selectedOption === ProjectRegisterMetrics.MarginalAbatementCostCurve) {
      return macData
        .map((item) => normalizeMeasureLabel(item?.reductionMeasureName))
        .filter((label) => label.length > 0);
    }

    if (selectedOption === ProjectRegisterMetrics.TimelineandFinancialMetrics) {
      const firstDataPoint = tlfMetricsData[0];
      if (!firstDataPoint) {
        return [];
      }

      return Object.keys(firstDataPoint)
        .filter((key) => key !== "year")
        .map((key) => normalizeMeasureLabel(key))
        .filter((label) => label.length > 0);
    }

    if (selectedOption === ProjectRegisterMetrics.MeasureImpactsbyScope) {
      return mibScopeData
        .map((item) => ({
          label: normalizeMeasureLabel(item?.label),
          barType: item?.barType,
        }))
        .filter(({ label, barType }) => {
          if (barType && barType !== "reduction") {
            return false;
          }

          return label.length > 0 && !NON_MEASURE_WATERFALL_LABELS.has(label);
        })
        .map(({ label }) => label);
    }

    // Target Trajectory does not have measure series labels to align table row order.
    return [];
  }, [selectedOption, macData, tlfMetricsData, mibScopeData]);

  // --- Hold a ref to the chart component ---
  const macCurveRef = useRef<MarginalAbatementCostCurveRef>(null);
  const measureImpactsRef = useRef<MeasureImpactsbyScopeRef>(null);
  const timelineFinancialRef = useRef<TimelineAndFinancialMetricsRef>(null);
  const glidepathRef = useRef<InteractiveGlidepathRef>(null);

  // --- Download handler in parent ---
  const handleDownload = (format: "png" | "jpeg") => {
    console.log("Download requested in format:", format);
    // Only call download on the displayed chart
    if (selectedOption === ProjectRegisterMetrics.MarginalAbatementCostCurve && macCurveRef.current) {
      macCurveRef.current.downloadChart(format);
    }
    if (selectedOption === ProjectRegisterMetrics.MeasureImpactsbyScope && measureImpactsRef.current) {
      measureImpactsRef.current.downloadChart(format);
    }
    if (selectedOption === ProjectRegisterMetrics.TimelineandFinancialMetrics && timelineFinancialRef.current) {
      timelineFinancialRef.current.downloadChart(format);
    }
    if (selectedOption === ProjectRegisterMetrics.InteractiveGlidepath && glidepathRef.current) {
      glidepathRef.current.downloadChart(format);
    }
  };

  // Handler for changing the selected option
  const handleOptionChange = (event: SelectChangeEvent<string>) => {
    const nextOption = event.target.value;
    setSelectedOption(nextOption);

    setFilterState((prevState) => ({
      ...prevState,
      dashboardSection: nextOption,
      ...(nextOption === ProjectRegisterMetrics.MeasureImpactsbyScope
        ? { groupBy: selectedGroupBy }
        : {}),
    }));
  };

  /**
   * Handles changes to dashboard filter values.
   *
   * @param {keyof FilterMetric} key - The filter key.
   * @param {string | null | number | string[]} value - The new filter value.
   */
  const handleFilterChange = (
    key: keyof FilterMetric,
    value: string | null | number | string[]
  ) => {
    const newValue = value === "0" ? null : value;

    setFilterState((prevState) => ({
      ...prevState,
      [key]: newValue,
    }));
  };

  /**
   * Toggles the visibility of filters.
   */
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  /**
   * Fetches the marginal abatement cost data.
   */
  const getMarginalAbatementCost = async () => {
    try {
      setLoading(true);

      const res = await _getMarginalAbatementCost(filterState);

      const safeMarginalAbatementCost = Array.isArray(res?.marginalAbatementCost)
        ? res.marginalAbatementCost
        : [];

      const safeMacGraphModelList = Array.isArray(res?.maCgraphModelList)
        ? res.maCgraphModelList
        : [];

      const safeImpactByScopeModelList = Array.isArray(res?.impactByScopeModelList)
        ? res.impactByScopeModelList
        : [];

      const safeTimelineFinancialMetric = Array.isArray(res?.timelineFinancialMetric)
        ? res.timelineFinancialMetric
        : [];

      const safeInteractiveGlidepathMetric = Array.isArray(res?.interactiveGlidepathMetric)
        ? res.interactiveGlidepathMetric
        : [];

      const safeMetrics = res?.metrics ?? {};

      if (
        selectedOption === ProjectRegisterMetrics.MarginalAbatementCostCurve
      ) {
        setMacData(
          getMacGraphDataByGroup(safeMacGraphModelList, selectedGroupBy)
        );
        setIsMacDataLoaded(true);
        initializeEmissionAndAbatement({
          marginalAbatementCost: safeMarginalAbatementCost,
        });
      }

      if (
        selectedOption === ProjectRegisterMetrics.TimelineandFinancialMetrics
      ) {
        const groupedTimelineFinancialMetric = getTimelineFinancialMetricByGroup(
          safeTimelineFinancialMetric,
          selectedGroupBy
        );

        const startYear = filterState.startYear ?? baseYear;
        const endYear = filterState.endYear ?? targetYear;

        const safeRes = res && typeof res === "object" ? res : {};

        const data = generateData(
          {
            ...safeRes,
            timelineFinancialMetric: groupedTimelineFinancialMetric,
          },
          false,
          Number(startYear),
          Number(endYear)
        );

        setTlfMetricsData(Array.isArray(data) ? data : []);
        setIsDataLoaded(true);
      }

      if (selectedOption === ProjectRegisterMetrics.MeasureImpactsbyScope) {
        const data: MIBScopeData[] = safeImpactByScopeModelList.map((item: any) => {
          const mibScopeData: MIBScopeData = {
            label: item?.reductionMeasureName ?? "Unknown",
            value: Number(item?.emissionReduction ?? 0),
            measures: Array.isArray(item?.measures) ? item.measures : [],
            scopeByEmissions: Array.isArray(item?.scopeByEmissions) ? item.scopeByEmissions : [],
          };
          return mibScopeData;
        });
        setMibScopeData(data);
      }

      setMarginalAbatementCost(safeMarginalAbatementCost);

      setMetricsData((prevMetricsData) =>
        prevMetricsData.map((metric) => ({
          ...metric,
          value: safeMetrics?.[metric.key] ?? 0,
          ...(metric.key === "averageAnnualRoi" && {
            isAverageAnnualRoiPresent: safeMetrics?.isAverageAnnualRoiPresent !== false,
          }),
        }))
      );

      if (selectedOption === ProjectRegisterMetrics.InteractiveGlidepath) {
        setInteractiveGlidepathData(safeInteractiveGlidepathMetric);
      }
    } catch (err: any) {
      console.error("Error fetching project register data:", err);
      // Non-ApiError = runtime throw that never passed through apiHandler; 400 = apiHandler is silent for it.
      if (!(err instanceof ApiError) || err.status === 400) {
        NotificationHelper.notifyError(err.message ?? "Unable to load project register data.");
      }
      setMacData([]);
      setMibScopeData([]);
      setTlfMetricsData([]);
      setInteractiveGlidepathData([]);
      setMarginalAbatementCost([]);
    } finally {
      setLoading(false);
    }
  };

  const initializeEmissionAndAbatement = (res: any) => {
    // Preserve user-committed range filters instead of reinitializing from filtered data.
    if (hasCommittedRangeFilters(filterState)) {
      return;
    }

    const list = Array.isArray(res?.marginalAbatementCost)
      ? res.marginalAbatementCost
      : [];

    const emissionReductions = list
      .map((item: any) => Number(item?.emissionReduction))
      .filter((value: number) => Number.isFinite(value));

    const marginalAbatementCosts = list
      .map((item: any) => Number(item?.mac))
      .filter((value: number) => Number.isFinite(value));

    if (emissionReductions.length === 0 || marginalAbatementCosts.length === 0) {
      setMinEmission(0);
      setMaxEmission(100);
      setEmissionReduction([0, 100]);
      setEmissionInputs({ min: "0", max: "100" });

      setMinMarginal(0);
      setMaxMarginal(100);
      setMarginalAbatement([0, 100]);
      setMarginalInputs({ min: "0", max: "100" });
      return;
    }

    let minEmission = Math.min(...emissionReductions);
    let maxEmission = Math.max(...emissionReductions);
    let minMarginalAbatementCost = Math.min(...marginalAbatementCosts);
    let maxMarginalAbatementCost = Math.max(...marginalAbatementCosts);

    if (minEmission === 0 && maxEmission === 0) {
      maxEmission = 100;
    }

    if (minMarginalAbatementCost === 0 && maxMarginalAbatementCost === 0) {
      maxMarginalAbatementCost = 100;
    }

    setMinEmission(minEmission);
    setMaxEmission(maxEmission);
    setEmissionReduction([minEmission, maxEmission]);
    setEmissionInputs({ min: String(minEmission), max: String(maxEmission) });

    setMinMarginal(minMarginalAbatementCost);
    setMaxMarginal(maxMarginalAbatementCost);
    setMarginalAbatement([minMarginalAbatementCost, maxMarginalAbatementCost]);
    setMarginalInputs({
      min: String(minMarginalAbatementCost),
      max: String(maxMarginalAbatementCost),
    });
  };

  const handleScopeChange = (selectedOptions: number[]) => {
    setSelectedScope(selectedOptions);
    const scopeLabels = scopesCoveredOptions
      .filter((option) => selectedOptions.includes(option.id))
      .map((option) => option.value);
    setFilterState((prevState) => ({
      ...prevState,
      scopes: selectedOptions,
      ghgScope: scopeLabels,
    }));
  };

  const updateEmissionRange = (nextRange: number[]) => {
    const [minValue, maxValue] = nextRange;
    setEmissionReduction(nextRange);
    setEmissionInputs({ min: String(minValue), max: String(maxValue) });
    setFilterState((prevState) => ({
      ...prevState,
      minEmissionReduction: String(minValue),
      maxEmissionReduction: String(maxValue),
    }));
  };

  const updateMarginalRange = (nextRange: number[]) => {
    const [minValue, maxValue] = nextRange;
    setMarginalAbatement(nextRange);
    setMarginalInputs({ min: String(minValue), max: String(maxValue) });
    setFilterState((prevState) => ({
      ...prevState,
      minMAC: String(minValue),
      maxMAC: String(maxValue),
    }));
  };

  const handleRangeInputChange = (
    range: "emission" | "marginal",
    field: "min" | "max",
    value: string
  ) => {
    const setInputs = range === "emission" ? setEmissionInputs : setMarginalInputs;
    setInputs((prevState) => getNextRangeInputState(prevState, field, value));
  };

  const commitRangeInput = (
    range: "emission" | "marginal",
    field: "min" | "max",
    rawValue?: string
  ) => {
    const isEmission = range === "emission";
    const currentRange = isEmission ? emissionReduction : marginalAbatement;
    const absoluteMin = isEmission ? minEmission : minMarginal;
    const absoluteMax = isEmission ? maxEmission : maxMarginal;
    const inputs = isEmission ? emissionInputs : marginalInputs;
    const updateRange = isEmission ? updateEmissionRange : updateMarginalRange;
    const nextRange = getCommittedRange(
      currentRange,
      inputs,
      field,
      absoluteMin,
      absoluteMax,
      rawValue
    );

    updateRange(nextRange);
  };

  const fetchDialogData = async (
    groupBy: string
  ): Promise<import("./data/MIBScopeData").MIBScopeData[]> => {
    try {
      const res = await _getMarginalAbatementCost({ ...filterState, groupBy });
      const list = Array.isArray(res?.impactByScopeModelList)
        ? res.impactByScopeModelList
        : [];

      return list.map((item: any) => ({
        label: item?.reductionMeasureName ?? "Unknown",
        value: Number(item?.emissionReduction ?? 0),
        measures: Array.isArray(item?.measures) ? item.measures : [],
      }));
    } catch (err) {
      console.error("Error fetching dialog measure data:", err);
    }
    return [];
  };

  useEffect(() => {
    getMarginalAbatementCost();
    setIsDataLoaded(false);
    setIsMacDataLoaded(false);
  }, [filterState]);

  const fetchLocationDetails = async () => {
    try {
      const res = await _getLocation(Number(LocationId));
      if (res) {
        setBaseYear(res.baseYear);
        setTargetYear(res.targetYear);
        const scenario = res.scenarios ?? [];
        setScenarioOptions(scenario);
      }
    } catch (err: any) {
      console.error("Error:", err);
      // Non-ApiError = runtime throw that never passed through apiHandler; 400 = apiHandler is silent for it.
      if (!(err instanceof ApiError) || err.status === 400) {
        NotificationHelper.notifyError(err.message ?? "Unable to load location details.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMarginalAbatementCost();
  }, []);

  useEffect(() => {
    fetchLocationDetails();
  }, []);

  useEffect(() => {
    if (!isGroupByLoading) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setIsGroupByLoading(false);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [isGroupByLoading, selectedGroupBy]);

  const compactSelectSx = {
    borderRadius: "6px",
    backgroundColor: "#fff",
    fontSize: "0.85rem",
    minHeight: 35,
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "#d1d5db",
    },
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      py: 0.75,
    },
  };

  const compactMenuProps = {
    MenuListProps: { dense: true },
    PaperProps: {
      sx: {
        "& .MuiMenuItem-root": {
          fontSize: "0.85rem",
          minHeight: 32,
          py: 0.25,
          px: 1,
        },
      },
    },
  };

  const filterControlSx = {
    minWidth: 180,
    flex: "0 0 auto",
  };

  const rangeFilterSx = {
    flex: "1 1 360px",
    minWidth: { xs: "100%", md: 0 },
    px: 1.5,
    py: 1.25,
    borderRadius: "16px",
    border: "1px solid #dbe3ee",
    background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 55%, #f1f5f9 100%)",
    boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
  };

  const compactTextFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      backgroundColor: "#fff",
      minHeight: 35,
      fontSize: "0.85rem",
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: "#d1d5db",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E57A1F",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#E57A1F",
      },
    },
    "& .MuiInputBase-input": {
      py: 0.9,
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: "#E57A1F",
    },
  };

  const formatRangeHint = (absoluteMin: number, absoluteMax: number) =>
    `Allowed: ${absoluteMin} to ${absoluteMax}`;

  const renderRangeFilter = ({
    title,
    subtitle,
    accentGradient,
    accentColor,
    range,
    inputs,
    absoluteMin,
    absoluteMax,
    minTestId,
    maxTestId,
  }: RangeFilterConfig) => (
    <Box sx={rangeFilterSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box>
          <Typography sx={{ color: '#1e293b', fontSize: '0.82rem', fontWeight: 700, lineHeight: 1.2 }}>
            {title}
          </Typography>
          <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.2 }}>
            {subtitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 34, height: 6, borderRadius: '999px', background: accentGradient }} />
          <Box sx={{ height: 8, width: 8, borderRadius: '999px', backgroundColor: accentColor, flexShrink: 0 }} />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
        <TextField
          size="small"
          type="number"
          label="Min"
          value={inputs.min}
          onChange={(e) => handleRangeInputChange(range, 'min', e.target.value)}
          onBlur={(e) => commitRangeInput(range, 'min', (e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitRangeInput(range, 'min', (e.target as HTMLInputElement).value);
            }
          }}
          inputProps={{ min: absoluteMin, max: absoluteMax, step: 'any' }}
          data-testid={minTestId}
          sx={compactTextFieldSx}
        />
        <TextField
          size="small"
          type="number"
          label="Max"
          value={inputs.max}
          onChange={(e) => handleRangeInputChange(range, 'max', e.target.value)}
          onBlur={(e) => commitRangeInput(range, 'max', (e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              commitRangeInput(range, 'max', (e.target as HTMLInputElement).value);
            }
          }}
          inputProps={{ min: absoluteMin, max: absoluteMax, step: 'any' }}
          data-testid={maxTestId}
          sx={compactTextFieldSx}
        />
      </Box>
      <Typography sx={{ color: '#64748b', fontSize: '0.72rem', mt: 0.9 }}>
        {formatRangeHint(absoluteMin, absoluteMax)}
      </Typography>
    </Box>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflow: 'visible', position: 'relative' }}>
      <Box
        sx={{
          transform: 'scale(0.75)',
          transformOrigin: 'top left',
          width: 'calc(100% / 0.75)',
          minHeight: '100vh',
        }}
      >
      <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%', mt: 0 }}>
      
      <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
        <Box
          sx={{
            mt: 0.5,
            mb: 2,
            width: '100%',
            maxWidth: '100%',
            overflow: 'visible',
            boxSizing: 'border-box',
            borderBottom: '1px solid #e5e7eb',
            pb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              flexWrap: 'wrap',
              width: '100%',
            }}
          >
            <Box
              component="button"
              type="button"
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                background: "none",
                border: "none",
                padding: 0,
                gap: "6px",
              }}
              data-testid="toggleFiltersButon"
              onClick={toggleFilters}
              aria-pressed={showFilters}
            >
              <FilterListIcon sx={{ color: '#E57A1F', fontSize: 20 }} />
              <Typography
                variant="body1"
                sx={{
                  color: '#475569',
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }}
                data-testid="filtersLabel"
              >
                Filters:
              </Typography>
              <Box sx={{ color: '#64748b', display: 'flex', alignItems: 'center' }}>
                {showFilters ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </Box>
            </Box>
            {showFilters && (
              <>
                <FormControl size="small" sx={{ ...filterControlSx, minWidth: 220 }}>
                  <Select
                    multiple
                    value={selectedScope}
                    onChange={(e) => {
                      const value = e.target.value as (number | string)[];
                      const allIds = scopesCoveredOptions.map((o) => o.id);
                      if (value.includes("all")) {
                        if (selectedScope.length !== allIds.length) {
                          handleScopeChange(allIds);
                        }
                        return;
                      }
                      const next = value as number[];
                      if (next.length === 0) {
                        return;
                      }
                      handleScopeChange(next);
                    }}
                    data-testid="scopeSelect"
                    MenuProps={compactMenuProps}
                    renderValue={(selected) =>
                      (selected as number[]).length === scopesCoveredOptions.length
                        ? "All Scopes"
                        : scopesCoveredOptions
                            .filter((o) => (selected as number[]).includes(o.id))
                            .map((o) => o.label)
                            .join(", ")
                    }
                    sx={compactSelectSx}
                  >
                    <MenuItem value="all">
                      <Checkbox
                        size="small"
                        checked={selectedScope.length === scopesCoveredOptions.length}
                        indeterminate={
                          selectedScope.length > 0 &&
                          selectedScope.length < scopesCoveredOptions.length
                        }
                        sx={{ p: 0.5 }}
                      />
                      All Scopes
                    </MenuItem>
                    {scopesCoveredOptions.map((option) => {
                      const isOnlySelected =
                        selectedScope.length === 1 &&
                        selectedScope[0] === option.id;
                      return (
                        <MenuItem
                          key={option.id}
                          value={option.id}
                          disabled={isOnlySelected}
                        >
                          <Checkbox
                            size="small"
                            checked={selectedScope.includes(option.id)}
                            sx={{ p: 0.5 }}
                          />
                          {option.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={filterControlSx}>
                    <Select
                      value={filterState.startYear ?? "0"}
                      onChange={(e) =>
                        handleFilterChange("startYear", e.target.value)
                      }
                      displayEmpty
                      MenuProps={compactMenuProps}
                      renderValue={(selected) =>
                        selected === "0" ? "Start Year" : selected
                      }
                      sx={compactSelectSx}
                    >
                      <MenuItem value="0">Start Year</MenuItem>
                      {baseYears.map((option, index) => (
                        <MenuItem key={index + option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={filterControlSx}>
                    <Select
                      value={filterState.endYear ?? "0"}
                      onChange={(e) =>
                        handleFilterChange("endYear", e.target.value)
                      }
                      displayEmpty
                      MenuProps={compactMenuProps}
                      renderValue={(selected) =>
                        selected === "0" ? "End Year" : selected
                      }
                      sx={compactSelectSx}
                    >
                      <MenuItem value="0">End Year</MenuItem>
                      {targetYears.map((option, index) => (
                        <MenuItem key={index + option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ ...filterControlSx, minWidth: 220 }}>
                    <Select
                      value={filterState.scenario ?? "0"}
                      onChange={(e) =>
                        handleFilterChange("scenario", e.target.value)
                      }
                      displayEmpty
                      MenuProps={compactMenuProps}
                      renderValue={(selected) =>
                        selected === "0" ? "Decarbonization plan" : selected
                      }
                      sx={compactSelectSx}
                    >
                      <MenuItem value="0">Decarbonization plan</MenuItem>
                      {scenarioOptions.map((scenario, index) => (
                        <MenuItem
                          key={index + (scenario ?? "")}
                          value={scenario}
                        >
                          {scenario}
                        </MenuItem>
                      ))}
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ ...filterControlSx, minWidth: 220 }}>
                  <Select
                    value={selectedGroupBy}
                    onChange={(e) => {
                      const value = e.target.value as ProjectRegisterGroupBy;
                      setIsGroupByLoading(true);
                      setSelectedGroupBy(value);
                      setFilterState((prevState) => ({
                        ...prevState,
                        groupBy: value,
                      }));
                    }}
                    displayEmpty
                    MenuProps={compactMenuProps}
                    renderValue={(selected) => {
                      const option = GROUP_BY_OPTIONS.find((item) => item.value === selected);
                      return `Group by ${option?.label ?? selected}`;
                    }}
                    sx={compactSelectSx}
                  >
                    {GROUP_BY_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {`Group by ${option.label}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
          </Box>
          {showFilters && (
            <Box
              sx={{
                mt: 1.5,
                display: 'flex',
                gap: 1.5,
                flexWrap: 'wrap',
                alignItems: 'stretch',
              }}
            >
              {renderRangeFilter({
                title: 'Emissions Reduction',
                subtitle: `${UNIT_TCO2E} range filter`,
                accentGradient: 'linear-gradient(90deg, #f59e0b 0%, #E57A1F 100%)',
                accentColor: '#E57A1F',
                range: 'emission',
                inputs: emissionInputs,
                absoluteMin: minEmission,
                absoluteMax: maxEmission,
                minTestId: 'emissionReductionMin',
                maxTestId: 'emissionReductionMax',
              })}
              {renderRangeFilter({
                title: 'Marginal Abatement Cost',
                subtitle: `$ / ${UNIT_TCO2E} range filter`,
                accentGradient: 'linear-gradient(90deg, #14b8a6 0%, #0f766e 100%)',
                accentColor: '#0f766e',
                range: 'marginal',
                inputs: marginalInputs,
                absoluteMin: minMarginal,
                absoluteMax: maxMarginal,
                minTestId: 'marginalAbatementMin',
                maxTestId: 'marginalAbatementMax',
              })}
            </Box>
          )}
          {isGroupByLoading && (
            <LinearProgress
              sx={{ mt: 1.5, borderRadius: 999, height: 4 }}
              data-testid="projectRegisterGroupByLoading"
            />
          )}
        </Box>
      </Grid>
      {loading ? (
        <Box
          sx={{
            width: '100%',
            minHeight: 320,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
          data-testid="projectRegisterLoading"
        >
          <CircularProgress size={48} />
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Loading project register data...
          </Typography>
        </Box>
      ) : (
        <>
          <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
            <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
              <ProjectMetrics
                metrics={metricsData}
                isPortfolioView={false}
                currency={currency}
                designVariant="portfolio"
              ></ProjectMetrics>
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
            <Paper variant="outlined" sx={{ mb: 1, width: '100%', overflow: 'hidden', p: 2.5, borderRadius: '12px', borderColor: '#d8dde3', backgroundColor: '#fff' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        mb: 5,
                        flexWrap: 'nowrap', // don't allow wrapping
                        width: '100%'
                      }}
                    >
                      <Grid
                        sx={{
                          fontSize: "0.95rem",
                          fontWeight: 600,
                          color: "#0f2b52",
                          whiteSpace: "nowrap",
                          flexGrow: 1
                        }}
                      >
                        {getChartTypeLabel(selectedOption)}
                      </Grid>
                      <ProjectRegisterDropdown
                        selectedOption={selectedOption}
                        handleOptionChange={handleOptionChange}
                        formControlSx={{ minWidth: 180, mr: 2, flexShrink: 1 }} // reduce minWidth if possible
                      />
                      <ChartDownloadButtonMenu
                        onDownload={handleDownload}
                        buttonLabels={{ png: "Download PNG", jpeg: "Download JPEG" }}
                        sx={{ mr: 0, flexShrink: 1 }}
                        tooltipText={`Download '${getChartTypeLabel(selectedOption)}' chart`}
                      />
                    </Box>

              <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
                {selectedOption ===
                  ProjectRegisterMetrics.MarginalAbatementCostCurve &&
                  isMacDataLoaded && (
                    <Box sx={{ width: '100%' }}>
                      <MarginalAbatementCostCurve
                        ref={macCurveRef}
                        data={macData}
                        labelKey="reductionMeasureName"
                        currency={currency}
                      />
                    </Box>
                  )}
                {selectedOption ===
                  ProjectRegisterMetrics.MeasureImpactsbyScope && (
                  <MeasureImpactsbyScope
                    ref={measureImpactsRef}
                    data={mibScopeData}
                    currency={currency}
                    groupBy={selectedGroupBy}
                    groupByOptions={GROUP_BY_OPTIONS}
                    onGroupByChange={(value) => {
                      setIsGroupByLoading(true);
                      setSelectedGroupBy(value as ProjectRegisterGroupBy);
                      setFilterState((prevState) => ({ ...prevState, groupBy: value }));
                    }}
                    fetchData={fetchDialogData}
                  />
                )}
                {selectedOption ===
                  ProjectRegisterMetrics.TimelineandFinancialMetrics &&
                  isDataLoaded && (
                    <TimelineAndFinancialMetrics
                      ref={timelineFinancialRef}
                      data={tlfMetricsData}
                      width={1100}
                      height={450}
                    />
                  )}
                {selectedOption === ProjectRegisterMetrics.InteractiveGlidepath && (
                  <InteractiveGlidepath ref={glidepathRef} emissionsData={interactiveGlidepathData} />
                )}
              </Grid>
            </Paper>
          </Grid>
          <Grid item xs={12} sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
            <Grid container spacing={0} sx={{ width: '100%', maxWidth: '100%' }}>
              <ProjectRegisterTable
                marginalAbatementCost={marginalAbatementCost}
                currency={currency}
                groupBy={selectedGroupBy}
                chartMeasureOrder={chartMeasureOrder}
              ></ProjectRegisterTable>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
      </Box>
    </Box>
  );
};

export default ProjectRegister;
